import {
  Component,
  computed,
  effect,
  HostListener,
  inject,
  input,
  OnDestroy,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ThemeService,
  WeekViewComponent,
  BookingFormComponent,
  RequestSuccessComponent,
  LoadingSpinnerComponent,
  PublicDedicatedCalendar,
  AggregatedSlot,
  BookingRequest,
  BookingState,
  PublicI18nService,
} from '@frontend/public-calendar';
import { DEFAULT_TIMEZONE } from '@frontend/util';
import {
  PreviewCalendarData,
  toPublicCalendar,
  generateMockSlots,
} from '../models/preview.models';

/**
 * Preview-enabled booking component for embedding in Page Editor.
 * Can operate in two modes:
 * 1. Preview mode: Receives data via `previewData` input, uses mock slots
 * 2. Live mode: Receives calendar via `calendar` input, fetches real slots from API
 */
@Component({
  selector: 'dcp-preview-booking',
  standalone: true,
  imports: [
    CommonModule,
    WeekViewComponent,
    BookingFormComponent,
    RequestSuccessComponent,
    LoadingSpinnerComponent,
  ],
  template: `
    <div
      class="min-h-full flex flex-col"
      [class.preview-mode]="isPreviewMode()"
      [style.--color-primary]="isPreviewMode() ? previewData()?.primaryColor : null"
      [style.--color-secondary]="isPreviewMode() ? previewData()?.secondaryColor : null"
      [style.--color-accent]="isPreviewMode() ? previewData()?.accentColor : null"
      [style.--color-background]="isPreviewMode() ? previewData()?.backgroundColor : null"
      [style.--color-surface]="isPreviewMode() ? previewData()?.surfaceColor : null"
      [style.--color-foreground]="isPreviewMode() ? previewData()?.textColor : null"
      [style.--color-muted]="isPreviewMode() ? previewData()?.textMutedColor : null"
      [style.background-color]="isPreviewMode() ? previewData()?.backgroundColor : null"
      [style.color]="isPreviewMode() ? previewData()?.textColor : null"
    >
      <main class="flex-1 w-full mx-auto px-4 py-8" [class]="containerWidthClass()">
        <!-- Loading state -->
        @if (state() === 'loading') {
          <pc-loading-spinner [message]="i18n.t('loading.calendar')" />
        }

        <!-- Error state -->
        @if (state() === 'error') {
          <div class="text-center py-12">
            <div class="mx-auto w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
              <svg class="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 class="text-xl font-semibold text-foreground mb-2">{{ i18n.t('error.notFound') }}</h2>
            <p class="text-muted mb-6">{{ errorMessage() }}</p>
          </div>
        }

        <!-- Success state -->
        @if (state() === 'success') {
          <pc-request-success
            [response]="bookingResponse()"
            [slot]="selectedSlot()"
            (newRequest)="resetToStart()"
          />
        }

        <!-- Slot selection & form - with ordered sections -->
        @if (state() === 'selecting' || state() === 'form' || state() === 'submitting') {
          <div class="space-y-6">
            <!-- Render all sections in order (by displayOrder) -->
            @for (section of orderedSections(); track section.id) {
              @switch (section.type) {
                @case ('Hero') {
                  <!-- Hero Section -->
                  <div class="rounded-lg border border-border bg-surface/50 p-6 sm:p-8">
                    @if (isSectionEmpty(section) && isPreviewMode()) {
                      <!-- Placeholder for empty hero section in preview mode -->
                      <div class="text-center text-muted-foreground">
                        <svg class="w-8 h-8 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"/>
                        </svg>
                        <p class="text-sm font-medium">{{ i18n.t('preview.heroPlaceholder') }}</p>
                        <p class="text-xs">{{ i18n.t('preview.heroPlaceholderHint') }}</p>
                      </div>
                    } @else {
                      @if (getSectionContent(section, 'title')) {
                        <h1 class="text-2xl font-bold text-foreground sm:text-3xl">{{ getSectionContent(section, 'title') }}</h1>
                      }
                      @if (getSectionContent(section, 'subtitle')) {
                        <p class="mt-2 text-lg text-muted">{{ getSectionContent(section, 'subtitle') }}</p>
                      }
                      @if (getSectionContent(section, 'ctaText') && getSectionContent(section, 'ctaUrl')) {
                        <div class="mt-6">
                          <a
                            [href]="getSectionContent(section, 'ctaUrl')"
                            class="inline-flex items-center px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors"
                          >
                            {{ getSectionContent(section, 'ctaText') }}
                          </a>
                        </div>
                      }
                    }
                  </div>
                }
                @case ('Calendar') {
                  <!-- Calendar Section -->
                  <div>
                    <h1 class="text-2xl font-bold mb-1">{{ displayCalendar()?.companyName }}</h1>
                    <p class="text-muted">{{ i18n.t('booking.selectSlot') }}</p>
                  </div>

                  <!-- Week View -->
                  <pc-week-view
                    [slots]="slots()"
                    [loading]="slotsLoading()"
                    [selectedSlot]="selectedSlot()"
                    [timezone]="timezone()"
                    (slotSelected)="onSlotSelected($event)"
                    (weekChanged)="onWeekChanged($event)"
                  />

                  <!-- Booking Form (disabled in preview mode) -->
                  @if ((state() === 'form' || state() === 'submitting') && !isPreviewMode()) {
                    <pc-booking-form
                      [selectedSlot]="selectedSlot()!"
                      [submitting]="state() === 'submitting'"
                      [errorMessage]="formErrorMessage()"
                      (submitRequest)="onSubmitRequest($event)"
                      (changeSlot)="clearSlotSelection()"
                    />
                  }

                  <!-- Preview mode: Show form preview without functionality -->
                  @if ((state() === 'form') && isPreviewMode()) {
                    <div class="rounded-lg border border-border bg-surface p-6 opacity-75">
                      <p class="text-center text-muted">{{ i18n.t('preview.formDisabled') }}</p>
                    </div>
                  }
                }
                @case ('TextBlock') {
                  <!-- Text Block Section -->
                  <div class="rounded-lg border border-border bg-surface/50 p-6 sm:p-8">
                    @if (getSectionContent(section, 'title')) {
                      <h2 class="text-xl font-bold text-foreground mb-2">{{ getSectionContent(section, 'title') }}</h2>
                    }
                    @if (getSectionContent(section, 'content')) {
                      <div class="prose prose-sm max-w-none text-foreground" [innerHTML]="getSectionContent(section, 'content')"></div>
                    }
                    @if (isSectionEmpty(section) && isPreviewMode()) {
                      <div class="text-center text-muted-foreground">
                        <p class="text-sm font-medium">Text Block</p>
                        <p class="text-xs">{{ i18n.t('preview.editToAddContent') }}</p>
                      </div>
                    }
                  </div>
                }
                @default {
                  <!-- Placeholder for other section types -->
                  <div class="rounded-lg border border-dashed border-border bg-surface/30 p-6">
                    <div class="text-center text-muted-foreground">
                      <p class="text-sm font-medium">{{ section.type }}</p>
                      <p class="text-xs">Section preview coming soon</p>
                    </div>
                  </div>
                }
              }
            }

            <!-- Fallback: Show calendar if no sections exist -->
            @if (orderedSections().length === 0) {
              <!-- Calendar header -->
              <div>
                <h1 class="text-2xl font-bold mb-1">{{ displayCalendar()?.companyName }}</h1>
                <p class="text-muted">{{ i18n.t('booking.selectSlot') }}</p>
              </div>

              <pc-week-view
                [slots]="slots()"
                [loading]="slotsLoading()"
                [selectedSlot]="selectedSlot()"
                [timezone]="timezone()"
                (slotSelected)="onSlotSelected($event)"
                (weekChanged)="onWeekChanged($event)"
              />

              @if ((state() === 'form' || state() === 'submitting') && !isPreviewMode()) {
                <pc-booking-form
                  [selectedSlot]="selectedSlot()!"
                  [submitting]="state() === 'submitting'"
                  [errorMessage]="formErrorMessage()"
                  (submitRequest)="onSubmitRequest($event)"
                  (changeSlot)="clearSlotSelection()"
                />
              }

              @if ((state() === 'form') && isPreviewMode()) {
                <div class="rounded-lg border border-border bg-surface p-6 opacity-75">
                  <p class="text-center text-muted">{{ i18n.t('preview.formDisabled') }}</p>
                </div>
              }
            }
          </div>
        }
      </main>

      <!-- Preview Mode Indicator -->
      @if (isPreviewMode() && showPreviewBadge()) {
        <div class="fixed top-2 right-2 z-50 px-2 py-1 bg-amber-500 text-white text-xs font-medium rounded">
          {{ i18n.t('preview.badge') }}
        </div>
      }
    </div>
  `,
  styles: [`
    :host {
      display: block;
      height: 100%;
    }

    .preview-mode {
      pointer-events: auto;
    }

    .preview-mode pc-booking-form {
      pointer-events: none;
    }
  `],
})
export class PreviewBookingComponent implements OnDestroy {
  private readonly themeService = inject(ThemeService);
  protected readonly i18n = inject(PublicI18nService);

  /**
   * Preview data input - when provided, component operates in preview mode
   * with mock slots and disabled form submission.
   */
  previewData = input<PreviewCalendarData | null>(null);

  /**
   * Live calendar data - when provided without previewData, component
   * expects slots to be provided via the slots input.
   */
  calendar = input<PublicDedicatedCalendar | null>(null);

  /**
   * External slots input for live mode.
   */
  externalSlots = input<AggregatedSlot[]>([]);

  /**
   * Whether to show the preview badge in preview mode.
   */
  showPreviewBadge = input<boolean>(true);

  /**
   * External loading state for slots.
   */
  externalSlotsLoading = input<boolean>(false);

  /**
   * External timezone from API response. Used for date/time calculations.
   */
  externalTimezone = input<string>(DEFAULT_TIMEZONE);

  // Output events for live mode
  weekChanged = output<{ from: string; to: string }>();
  slotSelected = output<AggregatedSlot>();
  submitRequest = output<BookingRequest>();

  // Computed: determine if we're in preview mode
  isPreviewMode = computed(() => this.previewData() !== null);

  // Computed: the calendar to display (from preview or live data)
  displayCalendar = computed<PublicDedicatedCalendar | null>(() => {
    const preview = this.previewData();
    if (preview) {
      return toPublicCalendar(preview);
    }
    return this.calendar();
  });

  // State
  state = signal<BookingState>('selecting');
  errorMessage = signal<string>('');
  formErrorMessage = signal<string | null>(null);

  // Data
  private internalSlots = signal<AggregatedSlot[]>([]);
  slotsLoading = signal<boolean>(false);
  selectedSlot = signal<AggregatedSlot | null>(null);
  bookingResponse = signal<{ requestId: string; trackingToken: string; trackingUrl: string } | null>(null);

  // Computed: use mock slots in preview mode, external slots otherwise
  slots = computed(() => {
    if (this.isPreviewMode()) {
      const preview = this.previewData();
      return preview?.mockSlots ?? generateMockSlots();
    }
    return this.externalSlots().length > 0 ? this.externalSlots() : this.internalSlots();
  });

  // Computed: timezone - use external timezone in live mode, default in preview mode
  timezone = computed(() => {
    if (this.isPreviewMode()) {
      return DEFAULT_TIMEZONE;
    }
    return this.externalTimezone();
  });

  // Computed: sections sorted by displayOrder (works in both preview and live mode)
  orderedSections = computed(() => {
    // Always read locale signal to establish dependency for reactive updates
    const locale = this.i18n.locale();

    const preview = this.previewData();
    if (preview?.sections) {
      // Preview mode: use sections from preview data (content already resolved)
      return [...preview.sections].sort((a, b) => a.displayOrder - b.displayOrder);
    }
    // Live mode: use sections from calendar API response
    const cal = this.calendar();
    if (cal?.sections) {
      return cal.sections
        .map(s => ({
          id: s.name,
          type: s.sectionType,
          displayOrder: s.sortOrder,
          isLocked: false,
          settings: s.settings,
          content: locale === 'en' ? s.contentEn : s.contentDe,
        }))
        .sort((a, b) => a.displayOrder - b.displayOrder);
    }
    return [];
  });

  // Check if there's a Hero section in page sections
  hasHeroSectionInPageSections = computed(() => {
    return this.orderedSections().some(s => s.type === 'Hero');
  });

  // Locale-aware hero content from page sections
  heroSection = computed(() => {
    const sections = this.orderedSections();
    const heroSection = sections.find(s => s.type === 'Hero');
    if (heroSection) {
      const content = heroSection.content as Record<string, string>;
      return {
        title: content['title'] || '',
        subtitle: content['subtitle'] || '',
        ctaText: content['ctaText'] || '',
        ctaUrl: content['ctaUrl'] || '',
      };
    }
    return null;
  });

  heroTitle = computed(() => this.heroSection()?.title || '');
  heroSubtitle = computed(() => this.heroSection()?.subtitle || '');

  // Show hero section if it has content OR if in preview mode with hero section placeholder
  showHeroSection = computed(() => {
    const section = this.heroSection();
    const hasContent = !!(section?.title || section?.subtitle);
    // In preview mode, show placeholder even if empty
    if (this.isPreviewMode() && this.hasHeroSectionInPageSections()) {
      return true;
    }
    return hasContent;
  });

  // Check if hero section is empty (for showing placeholder text in preview)
  heroSectionIsEmpty = computed(() => {
    const section = this.heroSection();
    return !(section?.title || section?.subtitle);
  });

  // Container width CSS class based on preview data
  containerWidthClass = computed(() => {
    const preview = this.previewData();
    const width = preview?.containerWidth || 'default';
    const map: Record<string, string> = {
      narrow: 'max-w-4xl',
      default: 'max-w-7xl',
      wide: 'max-w-[90rem]',
      full: 'max-w-full',
    };
    return map[width] || 'max-w-7xl';
  });

  currentYear = new Date().getFullYear();

  private themeApplied = false;

  constructor() {
    // Apply theme when calendar data changes - but only in LIVE mode
    // In preview mode, we apply scoped styles to avoid affecting the parent app
    effect(() => {
      const cal = this.displayCalendar();
      if (cal && !this.isPreviewMode()) {
        this.themeService.applyTheme(cal);
        this.themeApplied = true;
      }
    });

    // Sync external loading state
    effect(() => {
      this.slotsLoading.set(this.externalSlotsLoading());
    });
  }

  ngOnDestroy(): void {
    // Reset theme when component is destroyed (for embedded preview)
    if (this.themeApplied) {
      this.themeService.resetTheme();
    }
  }

  /**
   * Get scoped inline styles for preview mode to avoid affecting parent app theme.
   */
  protected getPreviewStyles(): Record<string, string> {
    const preview = this.previewData();
    if (!preview) return {};

    return {
      '--color-primary': preview.primaryColor,
      '--color-secondary': preview.secondaryColor,
      '--color-accent': preview.accentColor,
      '--color-background': preview.backgroundColor,
      '--color-surface': preview.surfaceColor,
      '--color-foreground': preview.textColor,
      '--color-muted': preview.textMutedColor,
    };
  }

  /**
   * Get content value from a section by key.
   */
  protected getSectionContent(section: { content: Record<string, unknown> }, key: string): string {
    return (section.content?.[key] as string) || '';
  }

  /**
   * Check if a section has no content (for placeholder display).
   */
  protected isSectionEmpty(section: { type: string; content: Record<string, unknown> }): boolean {
    const content = section.content;
    if (!content) return true;

    switch (section.type) {
      case 'Hero':
        return !content['title'] && !content['subtitle'];
      case 'TextBlock':
        return !content['title'] && !content['content'];
      default:
        return Object.keys(content).length === 0;
    }
  }

  onWeekChanged(range: { from: string; to: string }): void {
    // In preview mode, regenerate mock slots for the new week
    if (this.isPreviewMode()) {
      // Mock slots are already generated for current week
      return;
    }

    // Emit event for parent to handle in live mode
    this.weekChanged.emit(range);
  }

  onSlotSelected(slot: AggregatedSlot): void {
    this.selectedSlot.set(slot);
    this.state.set('form');
    this.formErrorMessage.set(null);
    this.slotSelected.emit(slot);
  }

  clearSlotSelection(): void {
    this.selectedSlot.set(null);
    this.state.set('selecting');
    this.formErrorMessage.set(null);
  }

  onSubmitRequest(request: BookingRequest): void {
    // Disabled in preview mode
    if (this.isPreviewMode()) return;

    // In live mode, emit event for parent to handle
    this.state.set('submitting');
    this.submitRequest.emit(request);
  }

  /** Called by parent to set success state after form submission */
  setSuccess(response: { requestId: string; trackingToken: string; trackingUrl: string }): void {
    this.bookingResponse.set(response);
    this.state.set('success');
  }

  /** Called by parent to set error state after form submission */
  setFormError(message: string): void {
    this.state.set('form');
    this.formErrorMessage.set(message);
  }

  resetToStart(): void {
    this.selectedSlot.set(null);
    this.bookingResponse.set(null);
    this.formErrorMessage.set(null);
    this.state.set('selecting');
  }

  setLanguage(locale: 'de' | 'en'): void {
    if (this.isPreviewMode()) return;
    this.i18n.setLocale(locale);
  }

  /**
   * Intercept clicks on anchor links (href="#...") to enable smooth scrolling.
   * This handles CTA buttons with anchor links pointing to sections.
   * Supports both old format (#calendar) and new format (#section-calendar).
   */
  @HostListener('click', ['$event'])
  onAnchorClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const anchor = target.closest('a');

    if (!anchor) return;

    const href = anchor.getAttribute('href');
    if (!href || !href.startsWith('#')) return;

    // Prevent default navigation for ALL anchor links
    event.preventDefault();
    event.stopPropagation();

    // Handle "#" (scroll to top)
    if (href === '#') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Find target element by ID
    const targetId = href.substring(1); // Remove the #
    let targetElement = document.getElementById(targetId);

    // Fallback: if not found and doesn't have section- prefix, try with prefix
    if (!targetElement && !targetId.startsWith('section-')) {
      targetElement = document.getElementById(`section-${targetId}`);
    }

    if (targetElement) {
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
}
